import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './PopoverShell.module.css';

/**
 * PopoverShell — a floating panel anchored under a parent surface.
 *
 * This is extracted from SegmentationUI's ToolPopoverShell and is intentionally UI-agnostic.
 * It supports optional outside-click and Escape handling (disabled when `pinned`).
 */
export function PopoverShell({
    open,
    left,
    rootWidth,
    strategy = 'absolute',
    anchorRect,
    placement = 'bottom-start',
    offset = 6,
    pinned = false,
    /** 'toolbar' stays below other layers; 'secondary' paints above. */
    layer = 'secondary',
    onClose,
    titleId,
    onMouseEnter,
    onMouseLeave,
    children,
    className = '',
    bodyClassName = '',
    style,
}) {
    const popoverRef = useRef(null);
    const [popoverWidth, setPopoverWidth] = useState(0);
    const [popoverHeight, setPopoverHeight] = useState(0);

    useEffect(() => {
        if (!open) return;
        const id = requestAnimationFrame(() => {
            const rect = popoverRef.current?.getBoundingClientRect?.();
            const w = rect?.width ?? 0;
            const h = rect?.height ?? 0;
            if (Number.isFinite(w) && w > 0) setPopoverWidth(w);
            if (Number.isFinite(h) && h > 0) setPopoverHeight(h);
        });
        return () => cancelAnimationFrame(id);
    }, [open, left, titleId, strategy, anchorRect, placement]);

    const clampedLeft = useMemo(() => {
        const max = Number(rootWidth ?? 0);
        const w = Number(popoverWidth ?? 0);
        const base = Number(left ?? 0);
        if (!Number.isFinite(max) || max <= 0) return base;
        if (!Number.isFinite(w) || w <= 0) return base;
        return Math.min(Math.max(0, base), Math.max(0, max - w));
    }, [left, popoverWidth, rootWidth]);

    const fixedPosition = useMemo(() => {
        if (strategy !== 'fixed') return null;
        if (!anchorRect) return null;

        const rect = anchorRect;
        const w = Number(popoverWidth ?? 0);
        const h = Number(popoverHeight ?? 0);
        const off = Number(offset ?? 0);
        const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
        const spacing = Number.isFinite(off) ? off : 6;

        let top = 0;
        let leftPx = 0;

        // Minimal placement support (enough for Progress page): bottom-start
        if (placement === 'bottom-start') {
            top = rect.bottom + spacing;
            leftPx = rect.left;
        } else if (placement === 'bottom-end') {
            top = rect.bottom + spacing;
            leftPx = rect.right - w;
        } else if (placement === 'top-start') {
            top = rect.top - h - spacing;
            leftPx = rect.left;
        } else if (placement === 'top-end') {
            top = rect.top - h - spacing;
            leftPx = rect.right - w;
        } else {
            top = rect.bottom + spacing;
            leftPx = rect.left;
        }

        // Clamp within viewport
        if (viewportW > 0 && w > 0) {
            leftPx = Math.min(Math.max(8, leftPx), Math.max(8, viewportW - w - 8));
        }
        if (viewportH > 0 && h > 0) {
            top = Math.min(Math.max(8, top), Math.max(8, viewportH - h - 8));
        }

        return { top, left: leftPx };
    }, [anchorRect, offset, placement, popoverHeight, popoverWidth, strategy]);

    useEffect(() => {
        if (!open) return undefined;
        if (pinned) return undefined;

        const onDocMouseDown = (e) => {
            const el = e.target;
            if (!(el instanceof Node)) return;
            if (popoverRef.current && popoverRef.current.contains(el)) return;
            onClose?.();
        };

        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            onClose?.();
        };

        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onClose, open, pinned]);

    if (!open) return null;

    const layerClass =
        strategy === 'fixed'
            ? styles.layerFloating
            : layer === 'toolbar'
              ? styles.layerToolbar
              : styles.layerSecondary;

    const rootClass =
        strategy === 'fixed'
            ? [styles.root, styles.rootFixed, layerClass, className].filter(Boolean).join(' ')
            : [styles.root, layerClass, className].filter(Boolean).join(' ');

    const popoverEl = (
        <div
            ref={popoverRef}
            className={rootClass}
            style={
                strategy === 'fixed'
                    ? { top: fixedPosition?.top ?? 0, left: fixedPosition?.left ?? 0, ...(style || {}) }
                    : { left: clampedLeft, ...(style || {}) }
            }
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className={[styles.body, bodyClassName].filter(Boolean).join(' ')} aria-labelledby={titleId}>
                {children}
            </div>
        </div>
    );

    if (strategy === 'fixed' && typeof document !== 'undefined') {
        return createPortal(popoverEl, document.body);
    }

    return popoverEl;
}

